import React, { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Settings, Trash2, Plus, Check, ChevronRight, Search } from 'lucide-react';
import { invitationLetterService, InvitationLetter } from '../services/invitationLetterService';
import { useToast } from '../contexts/ToastContext';
import useConfirmDialog from '../hooks/useConfirmDialog';
import './InvitationLetters.scss';

const InvitationLetters: React.FC = () => {
  const { showToast } = useToast();
  const { confirm, ConfirmDialogNode } = useConfirmDialog();
  const location = useLocation();
  // Detect if we're in employer route (either new route or legacy)
  const isEmployerRoute = location.pathname.startsWith('/employer/') || 
                         location.pathname === '/invitation-letters' ||
                         location.pathname === '/atsakymu-laiskai';

  const [letters, setLetters] = useState<InvitationLetter[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingLetter, setEditingLetter] = useState<InvitationLetter | null>(null);
  const [selectedLetters, setSelectedLetters] = useState<number[]>([]);
  const [searchFilter, setSearchFilter] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    content: ''
  });

  useEffect(() => {
    if (isEmployerRoute) {
      loadLetters();
    }
  }, [isEmployerRoute]);

  const loadLetters = async () => {
    try {
      setLoading(true);
      const data = await invitationLetterService.getAll();
      setLetters(data);
      setError(null);
    } catch (err: any) {
      console.error('Error loading invitation letters:', err);
      setError('Nepavyko įkelti atsakymų laiškų');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNew = () => {
    setEditingLetter(null);
    setFormData({ title: '', content: '' });
    setShowModal(true);
  };

  const handleEdit = (letter: InvitationLetter) => {
    setEditingLetter(letter);
    setFormData({
      title: letter.title,
      content: letter.content
    });
    setShowModal(true);
  };

  const handleDelete = async (id: number) => {
    if (!await confirm('Ar tikrai norite ištrinti šį laišką?')) return;

    try {
      await invitationLetterService.delete(id);
      await loadLetters();
    } catch (err: any) {
      console.error('Error deleting letter:', err);
      showToast('Nepavyko ištrinti laiško', 'error');
    }
  };

  const handleSave = async () => {
    if (!formData.title.trim() || !formData.content.trim()) {
      showToast('Prašome užpildyti visus laukus', 'warning');
      return;
    }

    try {
      if (editingLetter && editingLetter.id) {
        await invitationLetterService.update(editingLetter.id, formData);
      } else {
        await invitationLetterService.create(formData);
      }
      
      setShowModal(false);
      setFormData({ title: '', content: '' });
      setEditingLetter(null);
      await loadLetters();
    } catch (err: any) {
      console.error('Error saving letter:', err);
      showToast('Nepavyko išsaugoti laiško', 'error');
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    setFormData({ title: '', content: '' });
    setEditingLetter(null);
  };

  const filterLetters = (lettersList: InvitationLetter[]) => {
    if (!searchFilter.trim()) return lettersList;
    const search = searchFilter.toLowerCase();
    return lettersList.filter(letter => 
      letter.title.toLowerCase().includes(search)
    );
  };

  const handleToggleSelectAll = () => {
    const filtered = filterLetters(letters);
    if (selectedLetters.length === filtered.length && filtered.length > 0) {
      setSelectedLetters([]);
    } else {
      setSelectedLetters(filtered.map(l => l.id!));
    }
  };

  const handleToggleSelect = (id: number) => {
    setSelectedLetters(prev => 
      prev.includes(id) ? prev.filter(lid => lid !== id) : [...prev, id]
    );
  };

  const handleDeleteSelected = async () => {
    if (selectedLetters.length === 0) return;
    
    if (!await confirm(`Ar tikrai norite ištrinti ${selectedLetters.length} laiškų?`)) return;

    try {
      await Promise.all(selectedLetters.map(id => invitationLetterService.delete(id)));
      setSelectedLetters([]);
      loadLetters();
    } catch (error) {
      showToast('Įvyko klaida trinant laiškus', 'error');
    }
  };

  const filteredLetters = filterLetters(letters);

  if (!isEmployerRoute) {
    return (
      <div className="invitation-letters-page">
        <div className="container">
          <div className="error-message">
            <p>Ši funkcija prieinama tik darbdaviams</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="invitation-letters-page">
      {ConfirmDialogNode}
      <div className="container">
        <div className="header-row">
          <div className="breadcrumbs">
            <Link to="/">Titulinis</Link>
            <ChevronRight size={14} />
            <span>Atsakymų laiškai</span>
          </div>
          <div className="filters-toolbar">
            <div className="select-all-dropdown">
              <button 
                className="select-all-btn"
                onClick={handleToggleSelectAll}
              >
                <input 
                  type="checkbox" 
                  checked={selectedLetters.length > 0 && selectedLetters.length === filteredLetters.length}
                  onChange={handleToggleSelectAll}
                  onClick={(e) => e.stopPropagation()}
                />
                <span>Žymėti visus</span>
              </button>
            </div>
            <button 
              className="btn-delete"
              onClick={handleDeleteSelected}
              disabled={selectedLetters.length === 0}
            >
              <Trash2 size={16} />
              <span>Trinti</span>
            </button>
            <div className="search-input-wrapper">
              <input 
                type="text"
                className="search-title-input"
                placeholder="Laiško pavadinimas"
                value={searchFilter}
                onChange={(e) => setSearchFilter(e.target.value)}
              />
              <Search size={16} className="search-icon" />
            </div>
            <button className="btn-new" onClick={handleCreateNew}>
              <Plus size={16} />
              <span>Naujas atsakymo laiškas</span>
            </button>
          </div>
        </div>

      {loading ? (
        <div className="loading">Kraunama...</div>
      ) : error ? (
        <div className="error-message">{error}</div>
      ) : letters.length === 0 ? (
        <div className="no-data">
          <p>Jūs dar neturite sukurtų atsakymų laiškų</p>
          <button className="btn-secondary" onClick={handleCreateNew}>
            Sukurti pirmąjį laišką
          </button>
        </div>
      ) : (
        <div className="letters-grid">
          {filteredLetters.map((letter) => (
            <div key={letter.id} className={`letter-card ${selectedLetters.includes(letter.id!) ? 'selected' : ''}`}>
              <div className="letter-card-header">
                <div className="checkbox-wrapper">
                  <input
                    type="checkbox"
                    checked={selectedLetters.includes(letter.id!)}
                    onChange={() => handleToggleSelect(letter.id!)}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
                <div className="header-actions">
                  <button
                    className="btn-icon"
                    onClick={(e) => { e.stopPropagation(); handleEdit(letter); }}
                    title="Redaguoti"
                  >
                    <Settings size={18} />
                  </button>
                  <button
                    className="btn-icon"
                    onClick={(e) => { e.stopPropagation(); handleDelete(letter.id!); }}
                    title="Ištrinti"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
              <div className="letter-card-body">
                <h3 className="letter-title">{letter.title}</h3>
                <p className="letter-date">
                  Sukūrimo data: {new Date(letter.created_at!).toLocaleDateString('lt-LT')}
                </p>
                <p className="letter-preview">
                  {letter.content.length > 300
                    ? letter.content.substring(0, 300) + '...'
                    : letter.content}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={handleModalClose}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                <i className="fas fa-envelope"></i>{' '}
                {editingLetter ? 'Redaguoti laišką' : 'Naujas atsakymo laiškas'}
              </h2>
              <button className="btn-close" onClick={handleModalClose}>
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="modal-body">
              <div className="form-group">
                <label htmlFor="letter-title">
                  Pavadinimas <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="letter-title"
                  className="form-control"
                  placeholder="Pvz., Pakvietimas antram etapui"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                />
                <div className="help-text">
                  <i className="fas fa-question-circle"></i>
                  Pavadinimas tik Jums, kandidatai jo nematys
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="letter-content">
                  Laiško turinys <span className="required">*</span>
                </label>
                <textarea
                  id="letter-content"
                  className="form-control"
                  rows={10}
                  placeholder="Įveskite laiško tekstą..."
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                />
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-secondary" onClick={handleModalClose}>
                Atšaukti
              </button>
              <button className="btn-primary" onClick={handleSave}>
                <Check size={18} /> Išsaugoti
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default InvitationLetters;

